import moment from 'moment'
import _ from 'lodash'

/**
 * Geometry of the time-based axis, from the design (Figma "2026 - timeline request", node 3008:793).
 */

/**
 * Height of one month on the axis. Every month gets the same height whether it holds ten scans or
 * none, so an empty stretch reads as empty without pushing the rest of the treatment off screen.
 */
export const MONTH_HEIGHT = 152
/** The month label sits in a panel-coloured chip that masks the axis line behind it. */
export const MONTH_LABEL_HEIGHT = 12
/**
 * Keeps a marker at either end of a month clear of the labels above and below it.
 *
 * Half a marker is not enough: the selected one carries a ring outside its own outline, and a scan
 * on the last day of a month would otherwise paint over the next month's label.
 */
export const MONTH_PADDING = 13
/**
 * Diameter of a scan marker.
 *
 * Each marker also carries a ring of MARKER_OUTLINE in the panel colour, which is what separates
 * two of them where the axis brings them close enough to overlap.
 */
export const MARKER_SIZE = 15
/** The ring every marker carries, drawn outside its diameter. */
export const MARKER_OUTLINE = 2
/** The ring the selected marker carries, outside that one again. */
export const SELECTION_RING = 3
/**
 * The closest two markers are ever drawn.
 *
 * Markers that fall nearer than this on the axis are pushed apart to it, so they overlap by about
 * half and each keeps a crescent of its own to show and to hover. A month too crowded to hold them
 * all this far apart falls back to spreading them evenly, which goes closer.
 */
export const MIN_MARKER_DISTANCE = 7
/**
 * Scans landing within this of one another are one stack.
 *
 * A marker's own width: if two would touch at all, they belong to the same stack rather than being
 * drawn as separate ones that happen to overlap - which reads as a smear instead of a group.
 */
export const STACK_WINDOW = MARKER_SIZE
/**
 * How far each marker of a stack sits below the one in front of it.
 *
 * Deliberately less than a marker, so a stack overlaps into a single group the way the design draws
 * it. Between two stacks the gap is a whole marker, so they stay visibly apart.
 */
export const STACK_OFFSET = MIN_MARKER_DISTANCE
/**
 * How many markers a stack draws, however many scans it holds.
 *
 * The design caps it: two scans are drawn, and anything beyond them is counted on a third marker
 * rather than piling up. A stack of twenty would otherwise be a smear the height of a month.
 */
export const MAX_MARKERS_PER_STACK = 3
/**
 * A marker plus the room its selection ring needs, which is what every marker is positioned in.
 */
export const MARKER_FOOTPRINT = MARKER_SIZE + 2 * MARKER_OUTLINE + 2 * SELECTION_RING
/**
 * Paints the selected marker over every other one, wherever it sits.
 *
 * Markers overlap, so without this the scan being read loses part of its ring to whichever marker
 * happens to be drawn after it. Below the month labels, which still win.
 *
 * Nothing between a marker and the scrolling column opens a stacking context, so every marker is
 * compared against every other one and against the labels - not only against those of its own
 * month. Giving a month a z-index of its own would break that.
 */
export const SELECTED_MARKER_Z_INDEX = 100
/**
 * Paints the counted marker over the markers below it.
 *
 * It sits at the top of its stack, where the newest-in-front order would otherwise bury it: the
 * marker after it overlaps by more than half, which is enough to cover the figures it carries.
 */
export const COUNTED_MARKER_Z_INDEX = 50

const monthKey = date => moment(date).startOf('month').toISOString()

const getMonthBand = () => ({
  top: MONTH_LABEL_HEIGHT + MONTH_PADDING,
  bottom: MONTH_HEIGHT - MONTH_PADDING
})

/**
 * Every month between the first item and now, so months without scans are still drawn.
 *
 * The axis runs forwards: treatment starts at the top and the foot of it is now.
 *
 * @returns month start dates, oldest first.
 */
export const buildMonthRange = ({ items = [], now = moment() }) => {
  if (!items.length) {
    return []
  }

  const dates = items.map(item => moment(item.eventDate))
  const oldest = moment.min(dates).startOf('month')
  const newest = moment.max([...dates, moment(now)]).startOf('month')

  const months = []
  const cursor = moment(oldest)
  while (cursor.isSameOrBefore(newest)) {
    months.push(cursor.toISOString())
    cursor.add(1, 'month')
  }

  return months
}

/**
 * Where an item's date falls inside its month, in pixels from the top of that month's section.
 *
 * Within a month the axis runs forwards too: the month's label and its first day are at the top and
 * its last day at the foot, so the whole column reads oldest to newest.
 */
export const getOffsetInMonth = eventDate => {
  const date = moment(eventDate)
  const { top, bottom } = getMonthBand()
  const progress = (date.date() - 1) / date.daysInMonth()

  return top + progress * (bottom - top)
}

/**
 * Places one month's items on the axis as stacks, kept a minimum distance apart.
 *
 * Scans far enough apart are each their own stack of one, drawn at their own date. Scans too close
 * to draw apart share a stack and overlap in place rather than being moved to dates they did not
 * happen on. A month with no room left spreads its stacks evenly instead, which is what keeps every
 * marker inside its own month and in order.
 *
 * @param items timeline items of any type, within one month.
 * @returns `[{ offset, items }]`, oldest first.
 */
export const positionItems = (items = []) => {
  // Oldest first, down the axis. Sorting on the offset alone is not enough: it has day granularity,
  // so scans sharing a day share an offset and would keep whatever order they arrived in - which
  // put the patient's latest scan above the older ones taken the same day.
  const positioned = items
    .map(item => ({ item, offset: getOffsetInMonth(item.eventDate), at: moment(item.eventDate).valueOf() }))
    .sort((a, b) => a.offset - b.offset || a.at - b.at)

  if (!positioned.length) {
    return []
  }

  const { top, bottom } = getMonthBand()

  // Scans too close to draw apart become one stack, held at the date of the first of them.
  const stacks = positioned.reduce((grouped, entry) => {
    const open = _.last(grouped)

    if (open && entry.offset - open.offset < STACK_WINDOW) {
      open.items.push(entry.item)
      return grouped
    }

    return [...grouped, { offset: entry.offset, items: [entry.item] }]
  }, [])

  const drawnHeight = stack => (Math.min(stack.items.length, MAX_MARKERS_PER_STACK) - 1) * STACK_OFFSET
  const totalDrawnHeight = stacks.reduce((sum, stack) => sum + drawnHeight(stack), 0)

  const spreadEvenly = () => {
    const spacing = stacks.length > 1 ? (bottom - top - totalDrawnHeight) / (stacks.length - 1) : 0

    return stacks.reduce(
      ({ placed, cursor }, stack) => ({
        // Clamped rather than trusted: the running total accumulates float error, and the whole
        // point of this branch is that nothing leaves the month.
        placed: [...placed, { ...stack, offset: Math.min(cursor, bottom - drawnHeight(stack)) }],
        cursor: cursor + drawnHeight(stack) + Math.max(spacing, 0)
      }),
      { placed: [], cursor: top }
    ).placed
  }

  // Push each stack past the one in front of it, whole drawn height included, leaving a marker's
  // width between them so two stacks never run into one another.
  const nudged = stacks.reduce((placed, stack) => {
    const previous = _.last(placed)
    const earliest = previous ? previous.offset + drawnHeight(previous) + STACK_WINDOW : stack.offset

    return [...placed, { ...stack, offset: Math.max(stack.offset, earliest) }]
  }, [])

  const last = _.last(nudged)
  const overflow = last.offset + drawnHeight(last) - bottom
  if (overflow <= 0) {
    return nudged
  }

  const lift = Math.min(overflow, nudged[0].offset - top)
  const lifted = nudged.map(stack => ({ ...stack, offset: stack.offset - lift }))
  const lastLifted = _.last(lifted)

  // There is no headroom to lift into when a late-month stack already sits at the top, so a month
  // that still overflows is spread evenly instead. Without this a marker escapes into the month
  // below and paints over its label.
  return lastLifted.offset + drawnHeight(lastLifted) > bottom ? spreadEvenly() : lifted
}

/**
 * The markers a stack draws, and how many scans the first of them stands for.
 *
 * The axis runs forwards, so a stack draws its most recent scans and counts the older ones on the
 * marker above them: the recent history a doctor is looking for is the part drawn scan by scan.
 *
 * @param selectedItemId the scan being read, which always gets a marker of its own - its ring is
 * how the doctor finds it, and a stack deep enough to leave it among the counted showed no ring at
 * all. It takes the place just after the counted marker.
 * @returns `[{ item, offset, hiddenCount }]` - `hiddenCount` is 0 except on the first marker of a
 * stack that holds more than it can draw, where it counts the scans not drawn.
 */
export const getStackMarkers = (stack, selectedItemId) => {
  const drawn = Math.min(stack.items.length, MAX_MARKERS_PER_STACK)
  const hidden = stack.items.length - drawn
  const items = stack.items.slice(-drawn)

  const selected = hidden && selectedItemId && stack.items.find(item => item.id === selectedItemId)
  if (selected && !items.some(item => item.id === selectedItemId)) {
    items[Math.min(1, drawn - 1)] = selected
  }

  return items.map((item, i) => ({
    item,
    offset: stack.offset + i * STACK_OFFSET,
    // The first marker of an over-full stack stands for itself and everything before it.
    hiddenCount: hidden && i === 0 ? hidden + 1 : 0
  }))
}

/**
 * Lays the timeline out as months of equal height with items positioned by date inside them.
 *
 * @param items scan and missed-scan timeline items, in any order.
 * @param now reference point for the first month drawn.
 * @returns `[{ monthDate, stacks }]`, oldest month first.
 */
export default ({ items = [], now = moment() } = {}) => {
  const itemsByMonth = _.groupBy(items, item => monthKey(item.eventDate))

  return buildMonthRange({ items, now }).map(monthDate => ({
    monthDate,
    stacks: positionItems(itemsByMonth[monthDate] || [])
  }))
}
